import React, { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route } from 'react-router';
import { DashboardProvider, DASHBOARD_MODES } from './context/DashboardContext';
import { Layout } from './components/Layout/Layout';
import { initI18n, useTranslation } from '../../shared/i18n';
import { detectInAppBrowser, openInExternalBrowser } from '../../shared/utils/inAppBrowserDetection';

// Kid Dashboard Components
import { Dashboard as KidDashboard } from '../kid-dashboard/components/Dashboard/Dashboard';
import { Profile } from '../kid-dashboard/components/Profile/Profile';
import { Progress } from '../kid-dashboard/components/Progress/Progress';
import { SessionPage } from '../kid-dashboard/components/SessionPage/SessionPage';
import { WalletPage as KidWalletPage } from '../kid-dashboard/components/Wallet/WalletPage';
import { TransactionsPage as KidTransactionsPage } from '../kid-dashboard/components/Wallet/TransactionsPage';
import { AutoRewardRulesPage as KidAutoRewardRulesPage } from '../kid-dashboard/components/Wallet/AutoRewardRulesPage';
import { useDashboard as useKidDashboard } from '../kid-dashboard/hooks/useDashboard';
import { useActionCable } from '../kid-dashboard/hooks/useActionCable';

// Guardian Dashboard Components
import { Dashboard as GuardianDashboard } from '../guardian-dashboard/components/Dashboard/Dashboard';
import { GuardiansPage } from '../guardian-dashboard/components/GuardiansPage';
import { AddGuardianPage } from '../guardian-dashboard/components/AddGuardianPage';
import { FamilyCurrenciesPage } from '../guardian-dashboard/components/FamilyCurrenciesPage';
import { FamilyCurrencyTransactionsPage } from '../guardian-dashboard/components/FamilyCurrencyTransactionsPage';
import { AutoRewardRulesPage } from '../guardian-dashboard/components/AutoRewardRulesPage';
import { Referrals } from '../guardian-dashboard/components/Referrals/Referrals';
import { CurriculumPageWrapper } from '../guardian-dashboard/components/Curriculum/CurriculumPageWrapper';
import { ActivityCreatorWrapper } from '../guardian-dashboard/components/Activity/ActivityCreatorWrapper';
import { ActivityEditorWrapper } from '../guardian-dashboard/components/Activity/ActivityEditorWrapper';
import { WelcomePage, FamilyForm, ChildForm } from '../guardian-dashboard/components/Onboarding';
import { GoalSettingWrapper } from '../guardian-dashboard/components/GoalSetting';
import { KidSetupWizard } from '../guardian-dashboard/components/KidSetupWizard';
import { MonitoredActivitiesPage } from '../guardian-dashboard/components/MonitoredActivitiesPage';
import { MonitoredSessionsPage } from '../guardian-dashboard/components/MonitoredSessionsPage';
import { MonitoredActivityList as KidMonitoredActivityList } from '../kid-dashboard/components/MonitoredActivity/MonitoredActivityList';
import { MonitoredActivitySessionPage as KidMonitoredActivitySessionPage } from '../kid-dashboard/components/MonitoredActivity/MonitoredActivitySessionPage';

// Onboarding Component (for users without a family)
import NewFamilyApp from '../new-family/NewFamilyApp';

// Create a client with default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      retry: 2,
      staleTime: 30000 // 30 seconds
    }
  }
});

// Helper to get and clear flash message from sessionStorage
const getFlashFromStorage = () => {
  const flash = sessionStorage.getItem('flash_notice');
  if (flash) {
    sessionStorage.removeItem('flash_notice');
  }
  return flash;
};

// In-App Browser Warning Component for Kid Dashboard
const InAppBrowserWarning = () => {
  const { t } = useTranslation();

  const handleOpenExternalBrowser = () => {
    openInExternalBrowser(window.location.href);
  };

  return (
    <div className="d-flex min-vh-100 bg-light align-items-center justify-content-center">
      <div style={{ width: '448px', maxWidth: '90vw' }}>
        <div className="card border-0 shadow-lg rounded-4">
          <div className="card-body p-5 text-center">
            <div className="alert alert-warning mb-4" role="alert" data-testid="in-app-browser-blocked-message">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-exclamation-triangle me-2" viewBox="0 0 16 16">
                <path d="M7.938 2.016A.13.13 0 0 1 8.002 2a.13.13 0 0 1 .063.016.146.146 0 0 1 .054.057l6.857 11.667c.036.06.035.124.002.183a.163.163 0 0 1-.054.06.116.116 0 0 1-.066.017H1.146a.115.115 0 0 1-.066-.017.163.163 0 0 1-.054-.06.176.176 0 0 1 .002-.183L7.884 2.073a.147.147 0 0 1 .054-.057zm1.044-.45a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566z"/>
                <path d="M7.002 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 5.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995z"/>
              </svg>
              {t('children.sessions.in_app_browser_blocked')}
            </div>
            <button
              type="button"
              className="btn btn-primary w-100 py-2 fw-semibold"
              data-testid="open-external-browser-btn"
              onClick={handleOpenExternalBrowser}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                fontSize: '16px'
              }}>
              {t('children.sessions.open_external_browser')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Kid Dashboard Content
 * Handles kid-specific data loading and ActionCable integration
 */
const KidAppContent = ({ buildTime }) => {
  const { data, isLoading, isError, error, refetch } = useKidDashboard();
  const [flashMessage, setFlashMessage] = useState(() => getFlashFromStorage());

  // Check for in-app browser directly using our utility
  const inAppBrowserName = detectInAppBrowser();

  // Show in-app browser warning if detected
  if (inAppBrowserName) {
    return <InAppBrowserWarning />;
  }

  // Handle skip request approvals via ActionCable
  useActionCable(data?.child?.id, (approvalData) => {
    const message = approvalData?.message || 'Request approved';
    sessionStorage.setItem('flash_notice', message);
    window.location.href = '/children';
  });

  if (isLoading) {
    return (
      <div className="d-flex min-vh-100 bg-light align-items-center justify-content-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error loading dashboard</h4>
          <p>{error?.message || 'An unexpected error occurred'}</p>
          <button
            className="btn btn-danger"
            onClick={() => window.location.reload()}
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <DashboardProvider mode={DASHBOARD_MODES.KID} data={data}>
      <Layout flashNotice={flashMessage} setFlashNotice={setFlashMessage} buildTime={buildTime}>
        <Routes>
          <Route path="/" element={<KidDashboard data={data} onDataRefresh={refetch} />} />
          <Route path="/profile" element={<Profile data={data} />} />
          <Route path="/progress" element={<Progress />} />
          <Route path="/sessions/:sessionId" element={<SessionPage data={data} />} />
          <Route path="/wallet" element={<KidWalletPage />} />
          <Route path="/wallet/auto-reward-rules" element={<KidAutoRewardRulesPage />} />
          <Route path="/wallet/:currencyId/transactions" element={<KidTransactionsPage />} />
          <Route path="/monitored-activities" element={<KidMonitoredActivityList />} />
          <Route path="/monitored-sessions/:sessionId" element={<KidMonitoredActivitySessionPage />} />
        </Routes>
      </Layout>
    </DashboardProvider>
  );
};

/**
 * Guardian Dashboard Content
 * Handles guardian-specific routing
 * Shows onboarding flow (NewFamilyApp) if user has no family
 */
const GuardianAppContent = ({
  flashNotice: initialFlashNotice,
  flashAlert: initialFlashAlert,
  onboarding,
  locale,
  railsEnv,
  buildTime
}) => {
  const [flashNotice, setFlashNotice] = useState(() => initialFlashNotice || getFlashFromStorage());
  const [flashAlert, setFlashAlert] = useState(initialFlashAlert || null);
  const [hasFamily, setHasFamily] = useState(onboarding?.has_family ?? true);

  // Clear flash messages after display
  useEffect(() => {
    if (flashNotice) {
      const timer = setTimeout(() => setFlashNotice(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [flashNotice]);

  useEffect(() => {
    if (flashAlert) {
      const timer = setTimeout(() => setFlashAlert(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [flashAlert]);

  // Handle successful family creation
  const handleOnboardingComplete = () => {
    // Refresh the page to get new data, or update state
    setHasFamily(true);
    window.location.reload();
  };

  // Show onboarding flow if user doesn't have a family
  if (!hasFamily) {
    return (
      <NewFamilyApp
        locale={locale}
        railsEnv={railsEnv}
        currentPhoneNumber={onboarding?.current_phone_number || ''}
        hasPhoneNumber={onboarding?.has_phone_number || false}
        userName={onboarding?.user_name || ''}
        onComplete={handleOnboardingComplete}
      />
    );
  }

  return (
    <DashboardProvider mode={DASHBOARD_MODES.GUARDIAN}>
      <Routes>
        {/* Onboarding routes without layout */}
        <Route path="/welcome" element={<WelcomePage />} />
        <Route path="/family/new" element={<FamilyForm isEdit={false} />} />

        {/* Routes with layout */}
        <Route element={<Layout flashNotice={flashNotice} flashAlert={flashAlert} setFlashNotice={setFlashNotice} setFlashAlert={setFlashAlert} buildTime={buildTime} />}>
          <Route path="/" element={<GuardianDashboard />} />
          <Route path="/guardians" element={<GuardiansPage />} />
          <Route path="/guardians/new" element={<AddGuardianPage />} />
          <Route path="/family-currencies" element={<FamilyCurrenciesPage />} />
          <Route path="/family-currencies/auto-reward-rules" element={<AutoRewardRulesPage />} />
          <Route path="/family-currencies/:currencyId/transactions" element={<FamilyCurrencyTransactionsPage />} />
          <Route path="/monitored-activities" element={<MonitoredActivitiesPage />} />
          <Route path="/monitored-sessions" element={<MonitoredSessionsPage />} />
          <Route path="/referrals" element={<Referrals />} />
          <Route path="/family/edit" element={<FamilyForm isEdit={true} />} />
          <Route path="/children/new" element={<ChildForm isEdit={false} />} />
          <Route path="/children/:childId/edit" element={<ChildForm isEdit={true} />} />
          <Route path="/children/:childId/wizard" element={<KidSetupWizard />} />
          <Route path="/children/:childId/curriculum" element={<CurriculumPageWrapper />} />
          <Route path="/children/:childId/daily-target" element={<GoalSettingWrapper />} />
          <Route path="/children/:childId/activities/new" element={<ActivityCreatorWrapper />} />
          <Route path="/children/:childId/activities/:activityId/edit" element={<ActivityEditorWrapper />} />
        </Route>
      </Routes>
    </DashboardProvider>
  );
};

/**
 * Unified Dashboard App
 *
 * @param {Object} props
 * @param {string} props.mode - Dashboard mode: 'kid' or 'guardian'
 * @param {string} props.locale - Locale for i18n (default: 'vi')
 * @param {string} props.railsEnv - Rails environment
 * @param {string} props.flashNotice - Flash notice message (guardian mode)
 * @param {string} props.flashAlert - Flash alert message (guardian mode)
 * @param {Object} props.onboarding - Onboarding data (has_family, has_phone_number, current_phone_number)
 * @param {string} props.basePath - Override base path for BrowserRouter (e.g., '/native/guardian')
 * @param {string} props.buildTime - Build timestamp to display in dev mode
 */
export const App = ({
  mode = DASHBOARD_MODES.KID,
  locale = 'vi',
  railsEnv = 'production',
  flashNotice,
  flashAlert,
  onboarding,
  basePath: basePathOverride,
  buildTime
}) => {
  const [i18nReady, setI18nReady] = useState(false);
  const basePath = basePathOverride || (mode === DASHBOARD_MODES.KID ? '/children' : '/guardian');

  useEffect(() => {
    initI18n(locale);
    setI18nReady(true);
  }, [locale]);

  if (!i18nReady) {
    return (
      <div className="d-flex min-vh-100 bg-light align-items-center justify-content-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename={basePath}>
        {mode === DASHBOARD_MODES.KID ? (
          <div data-kid-dashboard="true">
            <KidAppContent buildTime={buildTime} />
          </div>
        ) : (
          <GuardianAppContent
            flashNotice={flashNotice}
            flashAlert={flashAlert}
            onboarding={onboarding}
            locale={locale}
            railsEnv={railsEnv}
            buildTime={buildTime}
          />
        )}
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
